import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Menu,
  MenuItem,
  IconButton,
  TableSortLabel,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { 
  Add as AddIcon, 
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Work as WorkIcon,
  PictureAsPdf as PdfIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';

const STATUS_COLORS = {
  'Created': 'default',
  'Sent': 'info',
  'Approved': 'success',
  'Rejected': 'error',
  'Converted to Job': 'warning'
};

export default function Estimates() {
  const navigate = useNavigate();
  const { estimates, loading, error, updateEstimate, addJob, deleteEstimate } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);

  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const handleMenuOpen = (event, estimate) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedEstimate(estimate);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedEstimate(null);
  };

  const handleDelete = async () => {
    if (selectedEstimate && window.confirm('Are you sure you want to delete this estimate?')) {
      try {
        await deleteEstimate(selectedEstimate.id);
        handleMenuClose();
      } catch (err) {
        console.error('Error deleting estimate:', err);
      }
    }
  };

  const handleApprove = async () => {
    if (selectedEstimate && window.confirm('Are you sure you want to approve this estimate?')) {
      try {
        // Update estimate status
        await updateEstimate(selectedEstimate.id, {
          ...selectedEstimate,
          status: 'Approved',
          updatedAt: new Date().toISOString()
        });

        // Create a new job
        await addJob({
          customerId: selectedEstimate.customerId,
          customerName: selectedEstimate.customerName,
          estimateId: selectedEstimate.id,
          description: selectedEstimate.description,
          total: selectedEstimate.total,
          status: 'Not Started',
          createdAt: new Date().toISOString(),
          createdBy: 'Admin',
          lineItems: selectedEstimate.lineItems,
          contactInfo: selectedEstimate.contactInfo
        });

        handleMenuClose();
      } catch (err) {
        console.error('Error approving estimate:', err);
      }
    }
  };

  const handleReject = async () => {
    if (selectedEstimate && window.confirm('Are you sure you want to reject this estimate?')) {
      try {
        await updateEstimate(selectedEstimate.id, {
          ...selectedEstimate,
          status: 'Rejected',
          updatedAt: new Date().toISOString()
        });
        handleMenuClose();
      } catch (err) {
        console.error('Error rejecting estimate:', err);
      }
    }
  };

  const handleConvertToJob = async () => {
    if (selectedEstimate && window.confirm('Are you sure you want to convert this estimate to a job?')) {
      try {
        const newJob = await addJob({
          customerId: selectedEstimate.customerId,
          customerName: selectedEstimate.customerName,
          estimateId: selectedEstimate.id,
          description: selectedEstimate.description,
          total: selectedEstimate.total,
          status: 'Not Started',
          createdAt: new Date().toISOString(),
          createdBy: 'Admin',
          lineItems: selectedEstimate.lineItems,
          contactInfo: selectedEstimate.contactInfo
        });
        
        // Update estimate status
        await updateEstimate(selectedEstimate.id, {
          ...selectedEstimate,
          status: 'Converted to Job',
          updatedAt: new Date().toISOString()
        });

        handleMenuClose();
        navigate(`/jobs/${newJob.id}`);
      } catch (err) {
        console.error('Error converting estimate to job:', err);
      }
    }
  };

  const filteredEstimates = estimates
    .filter(estimate =>
      estimate.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      estimate.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      estimate.id.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Convert to strings for comparison
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();

      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          Estimates
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/estimates/new')}
          fullWidth={false}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          New Estimate
        </Button>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        label="Search Estimates"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer component={Paper} sx={{ 
          width: '100%',
          minWidth: 800
        }}>
        <Table sx={{
          '& .MuiTableCell-root': {
            whiteSpace: { xs: 'nowrap', sm: 'normal' },
            wordBreak: 'break-word',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            padding: { xs: '8px', sm: '16px' }
          }
        }}>
          <TableHead>
            <TableRow>
              <TableCell width="15%">
                <TableSortLabel
                  active={sortField === 'estimateId'}
                  direction={sortField === 'estimateId' ? sortDirection : 'asc'}
                  onClick={() => handleSort('estimateId')}
                >
                  Estimate ID
                </TableSortLabel>
              </TableCell>
              <TableCell width="25%">
                <TableSortLabel
                  active={sortField === 'customerName'}
                  direction={sortField === 'customerName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('customerName')}
                >
                  Customer
                </TableSortLabel>
              </TableCell>
              <TableCell width="25%">
                <TableSortLabel
                  active={sortField === 'description'}
                  direction={sortField === 'description' ? sortDirection : 'asc'}
                  onClick={() => handleSort('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell width="15%">
                <TableSortLabel
                  active={sortField === 'total'}
                  direction={sortField === 'total' ? sortDirection : 'asc'}
                  onClick={() => handleSort('total')}
                >
                  Total
                </TableSortLabel>
              </TableCell>
              <TableCell width="10%">
                <TableSortLabel
                  active={sortField === 'status'}
                  direction={sortField === 'status' ? sortDirection : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell width="10%">
                <TableSortLabel
                  active={sortField === 'createdAt'}
                  direction={sortField === 'createdAt' ? sortDirection : 'asc'}
                  onClick={() => handleSort('createdAt')}
                >
                  Created At
                </TableSortLabel>
              </TableCell>
              <TableCell width="10%" align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEstimates.map((estimate) => (
              <TableRow 
                key={estimate.id}
                onClick={() => navigate(`/estimates/${estimate.id}`)}
                onContextMenu={(e) => handleMenuOpen(e, estimate)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <TableCell>{estimate.estimateId}</TableCell>
                <TableCell>{estimate.customerName}</TableCell>
                <TableCell>{estimate.description}</TableCell>
                <TableCell>${estimate.total.toFixed(2)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={estimate.status}
                      color={STATUS_COLORS[estimate.status]}
                      size="small"
                    />
                    {estimate.status === 'Converted to Job' && (
                      <Tooltip title="This estimate has been converted to a job and can only be deleted by administrators">
                        <WarningIcon color="warning" fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{formatDate(estimate.createdAt)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, estimate)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredEstimates.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {searchQuery ? 'No estimates found matching your search' : 'No estimates yet'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          navigate(`/estimates/${selectedEstimate?.id}`);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => {
          navigate(`/estimates/${selectedEstimate?.id}/edit`);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        {selectedEstimate?.status === 'Created' && (
          <MenuItem onClick={handleApprove}>
            <WorkIcon sx={{ mr: 1 }} /> Approve
          </MenuItem>
        )}
        {selectedEstimate?.status === 'Created' && (
          <MenuItem onClick={handleReject} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} /> Reject
          </MenuItem>
        )}
        {selectedEstimate?.status === 'Approved' && (
          <MenuItem onClick={handleConvertToJob}>
            <WorkIcon sx={{ mr: 1 }} /> Convert to Job
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          navigate(`/estimates/${selectedEstimate?.id}/pdf`);
          handleMenuClose();
        }}>
          <PdfIcon sx={{ mr: 1 }} /> Download PDF
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </Box>
  );
} 